import React, { useEffect, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, UploadCloud, Download, KeyRound, Users, Trash2, Mail, ShieldAlert, Database, Table2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PasskeySettings from "@/components/PasskeySettings";

const IMPORTS = [
  { kind: "members", label: "Base de socios EIM", cols: "rut, nombre, fecha_nacimiento, email, telefono, categoria, conyuge_nombre, conyuge_rut" },
  { kind: "children", label: "Hijos de socios", cols: "rut_titular, nombre, rut, fecha_nacimiento, colegio, relacion" },
  { kind: "ih-records", label: "Alumnos Instituto Hebreo", cols: "rut, condition" },
  { kind: "benefit-table", label: "Tabla de becas oficial", cols: "category, family_size, income_from, income_to, percentage" },
];

export default function AdminConfig() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuper = user && user.role === "superadmin";
  const [cfg, setCfg] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyImport, setBusyImport] = useState(null);
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", rut: "", role: "admin" });
  const [busyStaff, setBusyStaff] = useState(false);
  const [flags, setFlags] = useState([]);

  const loadFlags = async () => {
    try { const { data } = await api.get("/admin/moderation-flags"); setFlags(data); } catch { /* no superadmin */ }
  };

  const reactivate = async (id) => {
    if (!window.confirm("¿Marcar como falso positivo y reactivar la cuenta?")) return;
    try { await api.post(`/admin/moderation-flags/${id}/reactivate`); toast.success("Cuenta reactivada."); loadFlags(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const loadStaff = async () => {
    try { const { data } = await api.get("/admin/staff"); setStaff(data); } catch { /* no superadmin */ }
  };

  const createStaff = async () => {
    if (!newStaff.name || !newStaff.email || !newStaff.rut) { toast.error("Completa nombre, email y RUT."); return; }
    setBusyStaff(true);
    try {
      await api.post("/admin/staff", newStaff);
      toast.success("Invitación enviada. El usuario recibirá un correo para crear su contraseña.");
      setNewStaff({ name: "", email: "", rut: "", role: "admin" });
      loadStaff();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusyStaff(false); }
  };

  const deleteStaff = async (id, name) => {
    if (!window.confirm(`¿Eliminar a ${name || "este usuario"}? Esta acción no se puede deshacer.`)) return;
    try { await api.delete(`/admin/staff/${id}`); toast.success("Usuario eliminado."); loadStaff(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [genCodes, setGenCodes] = useState(null);
  const [tableVersion, setTableVersion] = useState("");
  const [busyReal, setBusyReal] = useState(false);
  const [realResult, setRealResult] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [busyUpload, setBusyUpload] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [busyExport, setBusyExport] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [busyPw, setBusyPw] = useState(false);

  const changePassword = async () => {
    if (pw.next.length < 8) { toast.error("La nueva contraseña debe tener al menos 8 caracteres."); return; }
    if (pw.next !== pw.confirm) { toast.error("Las contraseñas no coinciden."); return; }
    setBusyPw(true);
    try {
      await api.post("/admin/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Contraseña actualizada.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusyPw(false); }
  };

  const loadStatus = async () => {
    try { const { data } = await api.get("/admin/import/status"); setImportStatus(data); }
    catch (err) { /* silencioso: panel informativo */ }
  };

  const importRealBase = async () => {
    setBusyReal(true);
    try {
      const { data } = await api.post("/admin/import/real-base");
      setRealResult(data);
      toast.success("Base real importada correctamente.");
      loadStatus();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusyReal(false); }
  };

  const uploadMembersXlsx = async (file) => {
    if (!file) return;
    setBusyUpload(true); setUploadResult(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/import/members-xlsx", form);
      setUploadResult(data);
      toast.success(`Base actualizada: ${data.inserted} nuevos, ${data.updated} actualizados.`);
      loadStatus();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusyUpload(false); }
  };

  const downloadBaseExcel = async () => {
    setBusyExport(true);
    try {
      const res = await api.get("/admin/import/export/base.xlsx", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : "base_eim_completa.xlsx";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Excel descargado.");
    } catch (err) { toast.error("No se pudo generar el Excel."); } finally { setBusyExport(false); }
  };

  const load = async () => {
    try {
      const [c, cam] = await Promise.all([api.get("/admin/config"), api.get("/campaigns/active").catch(() => ({ data: null }))]);
      setCfg(c.data); setCampaign(cam.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); loadStatus(); }, []);
  useEffect(() => { if (isSuper) { loadStaff(); loadFlags(); } }, [isSuper]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/config", { income_tolerance_pct: Number(cfg.income_tolerance_pct), texts: cfg.texts, ih_social_worker_email: cfg.ih_social_worker_email || "", closing_summary_email: cfg.closing_summary_email || "" });
      if (campaign) await api.put("/admin/campaign", { name: campaign.name, opens_at: campaign.opens_at, closes_at: campaign.closes_at });
      toast.success("Configuración guardada.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!cfg) return <AdminLayout><div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AdminLayout>;

  const doImport = async (kind, file, version) => {
    if (!file) return;
    setBusyImport(kind);
    const form = new FormData();
    form.append("file", file);
    if (kind === "benefit-table") form.append("version", version || `Importada-${Date.now()}`);
    try {
      const { data } = await api.post(`/admin/import/${kind}`, form);
      const summary = data.inserted !== undefined
        ? `${data.inserted} nuevos, ${data.updated || 0} actualizados`
        : data.added !== undefined ? `${data.added} agregados`
        : data.rows !== undefined ? `${data.rows} filas` : `${data.processed || 0} procesados`;
      toast.success(`Importación "${kind}": ${summary}.`);
      if (data.errors?.length) toast.error(`${data.errors.length} filas con error. Primera: ${data.errors[0]}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusyImport(null); }
  };

  const downloadTemplate = async (kind) => {
    const token = localStorage.getItem("eim_token");
    const resp = await fetch(`${API_BASE}/admin/import/template/${kind}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `plantilla_${kind}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const regenCodes = async () => {
    try { const { data } = await api.post("/auth/mfa/backup-codes/regenerate"); setGenCodes(data.backup_codes); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold mb-6">Configuración</h1>
      <div className="space-y-6 max-w-2xl">
        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Campaña de postulaciones</h2>
          {campaign && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs uppercase font-semibold text-slate-500">Nombre</Label>
                <Input className="h-12 rounded-xl mt-1.5" value={campaign.name} onChange={(e) => setCampaign({ ...campaign, name: e.target.value })} data-testid="cfg-campaign-name" /></div>
              <div><Label className="text-xs uppercase font-semibold text-slate-500">Cierre</Label>
                <Input type="date" className="h-12 rounded-xl mt-1.5" value={(campaign.closes_at || "").slice(0, 10)} onChange={(e) => setCampaign({ ...campaign, closes_at: e.target.value })} /></div>
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Tolerancia de discrepancia de ingresos</h2>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">% de tolerancia entre ingreso declarado y detectado</Label>
            <Input type="number" className="h-12 rounded-xl mt-1.5 w-40" value={cfg.income_tolerance_pct} onChange={(e) => setCfg({ ...cfg, income_tolerance_pct: e.target.value })} data-testid="cfg-tolerance" />
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Textos</h2>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Texto de introducción (Inicio)</Label>
            <Textarea className="rounded-xl mt-1.5 min-h-[120px]" value={cfg.texts?.home_intro || ""} onChange={(e) => setCfg({ ...cfg, texts: { ...cfg.texts, home_intro: e.target.value } })} data-testid="cfg-home-intro" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Explicación estándar cuando la beca es menor a lo solicitado</Label>
            <p className="text-xs text-muted-foreground mt-1">Se precarga en el comentario de la decisión y se incluye en el correo de resultado al solicitante.</p>
            <Textarea className="rounded-xl mt-1.5 min-h-[120px]" value={cfg.texts?.diff_pct_explanation || ""} onChange={(e) => setCfg({ ...cfg, texts: { ...cfg.texts, diff_pct_explanation: e.target.value } })} data-testid="cfg-diff-explanation" />
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Instituto Hebreo</h2>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Email(s) de la asistente social IH (para consultas de beca)</Label>
            <Input type="text" className="h-12 rounded-xl mt-1.5" value={cfg.ih_social_worker_email || ""} placeholder="asistentesocial@institutohebreo.cl, otra@institutohebreo.cl"
              onChange={(e) => setCfg({ ...cfg, ih_social_worker_email: e.target.value })} data-testid="cfg-ih-email" />
            <p className="text-xs text-muted-foreground mt-1.5">Puedes ingresar más de un correo separados por coma (,) o punto y coma (;).</p>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Correo de resumen al cerrar solicitudes</h2>
          <p className="text-sm text-muted-foreground">Cada vez que se cierra (procesa) una solicitud, se envía un resumen a este correo para control interno. Puedes cambiarlo (por ejemplo, a Dana) cuando corresponda. Admite más de un correo separados por coma (,) o punto y coma (;).</p>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Email(s) de resumen</Label>
            <Input type="text" className="h-12 rounded-xl mt-1.5" value={cfg.closing_summary_email || ""} placeholder="becas@eimbecas.cl, dana@eimbecas.cl"
              onChange={(e) => setCfg({ ...cfg, closing_summary_email: e.target.value })} data-testid="cfg-closing-email" />
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <div className="flex items-center gap-2"><UploadCloud className="w-5 h-5 text-accent" /><h2 className="text-lg font-semibold">Base real EIM (un clic)</h2></div>
          <p className="text-sm text-muted-foreground">Carga la base oficial de socios/no socios (agrupada por titular, con marca Instituto Hebreo y estado vigente), los alumnos IH para el cruce automático, y el histórico de becas 2016–2024. Los archivos ya vienen incluidos en el sistema. Puedes ejecutarlo las veces que quieras: actualiza sin duplicar.</p>
          <Button onClick={importRealBase} disabled={busyReal} className="btn-lift h-12 rounded-xl px-8" data-testid="btn-import-real-base">
            {busyReal ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Importando base real…</> : "Importar base real ahora"}
          </Button>
          {realResult && (
            <div className="bg-secondary/60 rounded-xl p-4 text-sm space-y-1" data-testid="import-real-result">
              <p><strong>Socios:</strong> {realResult.members.inserted} nuevos, {realResult.members.updated} actualizados · {realResult.members.family_groups} grupos familiares · {realResult.members.ih_student_records} alumnos IH · {realResult.members.skipped_no_rut} sin RUT válido.</p>
              <p><strong>Historial de becas:</strong> {realResult.beca_history.total} registros ({Object.keys(realResult.beca_history.per_year || {}).length} años).</p>
            </div>
          )}
          <div className="border-t border-border pt-4 space-y-4">
            <div>
              <p className="text-sm font-semibold flex items-center gap-2"><UploadCloud className="w-4 h-4 text-accent" /> Subir base actualizada (Excel)</p>
              <p className="text-xs text-muted-foreground mt-1">Sube tu propio Excel (mismo formato oficial: hoja <strong>“Datos”</strong>, una fila por persona con <strong>RUT_TITULAR</strong>). Agrega socios nuevos y actualiza los existentes (incluida la columna <strong>VIGENTE</strong>) sin duplicar. A quienes no vengan en el archivo no se les toca.</p>
              <label className="mt-2 inline-flex items-center gap-2 border-2 border-dashed border-border rounded-xl px-4 py-2.5 cursor-pointer hover:bg-secondary/40 text-sm" data-testid="members-xlsx-label">
                {busyUpload ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                {busyUpload ? "Procesando Excel…" : "Elegir archivo .xlsx"}
                <input type="file" accept=".xlsx,.xls" className="hidden" disabled={busyUpload}
                  onChange={(e) => { uploadMembersXlsx(e.target.files?.[0]); e.target.value = ""; }} data-testid="members-xlsx-input" />
              </label>
              {uploadResult && (
                <p className="text-xs text-emerald-700 mt-2" data-testid="members-xlsx-result">
                  Listo: <strong>{uploadResult.inserted}</strong> nuevos, <strong>{uploadResult.updated}</strong> actualizados · {uploadResult.family_groups} grupos familiares · {uploadResult.ih_student_records} alumnos IH.
                </p>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold flex items-center gap-2"><Download className="w-4 h-4 text-accent" /> Descargar base completa (Excel)</p>
              <p className="text-xs text-muted-foreground mt-1">Planilla ordenada y con <strong>autofiltro</strong>: busca por apellido, RUT o correo. Incluye familia agrupada, socio vigente, IH, beca (Sí/No + % + año), postulantes nuevos y columnas reservadas para Deportes, Locker, Estacionamiento y Gimnasio.</p>
              <Button onClick={downloadBaseExcel} disabled={busyExport} variant="outline" className="mt-2 h-10 rounded-xl border-2" data-testid="btn-export-base">
                {busyExport ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generando…</> : <><Download className="w-4 h-4 mr-2" /> Descargar Excel</>}
              </Button>
            </div>
          </div>
          {importStatus && (
            <div className="rounded-xl border border-border bg-white p-4 space-y-3" data-testid="import-status-panel">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-accent" />
                <h3 className="text-sm font-semibold">Estado de la base cargada</h3>
              </div>
              {importStatus.last_import ? (
                <p className="text-xs text-muted-foreground" data-testid="import-status-last">
                  Último import: <strong>{new Date(importStatus.last_import.at).toLocaleString("es-CL")}</strong>
                  {importStatus.last_import.by ? <> · por {importStatus.last_import.by}</> : null}
                </p>
              ) : (
                <p className="text-xs text-amber-700" data-testid="import-status-never">Aún no se ha ejecutado la importación de la base real en este entorno.</p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg bg-secondary/60 p-3" data-testid="import-count-members">
                  <p className="text-lg font-bold text-primary">{importStatus.live.eim_members.toLocaleString("es-CL")}</p>
                  <p className="text-xs text-muted-foreground">Socios / titulares</p>
                </div>
                <div className="rounded-lg bg-secondary/60 p-3" data-testid="import-count-ih">
                  <p className="text-lg font-bold text-primary">{importStatus.live.ih_student_records.toLocaleString("es-CL")}</p>
                  <p className="text-xs text-muted-foreground">Alumnos IH</p>
                </div>
                <div className="rounded-lg bg-secondary/60 p-3" data-testid="import-count-beca">
                  <p className="text-lg font-bold text-primary">{importStatus.live.beca_history.toLocaleString("es-CL")}</p>
                  <p className="text-xs text-muted-foreground">Becas históricas</p>
                </div>
                <div className="rounded-lg bg-secondary/60 p-3" data-testid="import-count-table">
                  <p className="text-lg font-bold text-primary">{importStatus.live.active_benefit_table_rows.toLocaleString("es-CL")}</p>
                  <p className="text-xs text-muted-foreground">Filas tabla activa</p>
                </div>
              </div>
              {importStatus.live.active_benefit_table && (
                <p className="text-xs text-muted-foreground" data-testid="import-status-table-version">
                  Tabla de beneficios activa: <strong>{importStatus.live.active_benefit_table}</strong>
                </p>
              )}
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <div className="flex items-center gap-2"><UploadCloud className="w-5 h-5 text-accent" /><h2 className="text-lg font-semibold">Importar datos por CSV</h2></div>
          <p className="text-sm text-muted-foreground">Sube tu base real de socios, hijos, alumnos del Instituto Hebreo y la tabla de becas oficial. Descarga la plantilla para ver el formato exacto.</p>
          {IMPORTS.map((imp) => (
            <div key={imp.kind} className="border border-border rounded-xl p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold text-sm">{imp.label}</p>
                  <p className="text-xs text-muted-foreground">Columnas: {imp.cols}</p>
                </div>
                <button onClick={() => downloadTemplate(imp.kind)} className="text-primary text-xs font-semibold flex items-center gap-1" data-testid={`tpl-${imp.kind}`}>
                  <Download className="w-3 h-3" /> Plantilla
                </button>
              </div>
              {imp.kind === "benefit-table" && (
                <>
                  <Input className="h-10 rounded-lg mt-3" placeholder="Nombre de versión (ej: 2027-oficial)" value={tableVersion} onChange={(e) => setTableVersion(e.target.value)} data-testid="import-table-version" />
                  <p className="text-xs text-muted-foreground mt-2">Formatos aceptados: <strong>Excel (.xlsx/.xls), CSV, PDF o imagen (.jpg/.png)</strong>. El PDF/imagen se convierte automáticamente a la tabla de cálculo.</p>
                  <button onClick={() => navigate("/admin/tabla")} className="mt-2 text-primary text-xs font-semibold inline-flex items-center gap-1" data-testid="btn-ver-tabla-becas">
                    <Table2 className="w-3.5 h-3.5" /> Ver tabla de becas actual
                  </button>
                </>
              )}
              <input type="file" accept={imp.kind === "benefit-table" ? ".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png" : ".csv"} className="mt-3 block text-sm" disabled={busyImport === imp.kind}
                onChange={(e) => { doImport(imp.kind, e.target.files?.[0], tableVersion); e.target.value = ""; }}
                data-testid={`import-${imp.kind}`} />
              {busyImport === imp.kind && <p className="text-xs text-primary mt-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Importando…</p>}
            </div>
          ))}
        </section>

        {isSuper && (
          <section className="bg-white rounded-2xl border border-border p-6 space-y-4" data-testid="staff-admin-section">
            <div className="flex items-center gap-2"><Users className="w-5 h-5 text-accent" /><h2 className="text-lg font-semibold">Administradores y evaluadores</h2></div>
            <p className="text-sm text-muted-foreground">Solo tú (superadmin) puedes crear o eliminar cuentas del panel. Al crear una, le llega un correo para crear su contraseña; luego ingresa con su RUT + contraseña + código por email.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase font-semibold text-slate-500">Nombre</Label>
                <Input className="h-11 rounded-xl mt-1.5" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} data-testid="staff-name" /></div>
              <div><Label className="text-xs uppercase font-semibold text-slate-500">Email</Label>
                <Input type="email" className="h-11 rounded-xl mt-1.5" value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} data-testid="staff-email" /></div>
              <div><Label className="text-xs uppercase font-semibold text-slate-500">RUT</Label>
                <Input className="h-11 rounded-xl mt-1.5" placeholder="12.345.678-9" value={newStaff.rut} onChange={(e) => setNewStaff({ ...newStaff, rut: e.target.value })} data-testid="staff-rut" /></div>
              <div><Label className="text-xs uppercase font-semibold text-slate-500">Rol</Label>
                <Select value={newStaff.role} onValueChange={(v) => setNewStaff({ ...newStaff, role: v })}>
                  <SelectTrigger className="h-11 rounded-xl mt-1.5" data-testid="staff-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador (acceso completo)</SelectItem>
                    <SelectItem value="evaluator">Evaluador (solo revisa/resuelve)</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <Button onClick={createStaff} disabled={busyStaff} className="btn-lift h-11 rounded-xl" data-testid="btn-create-staff">
              {busyStaff ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-2" /> Crear e invitar por correo</>}
            </Button>
            <div className="border-t border-border pt-4 space-y-2">
              {staff.map((s) => (
                <div key={s.id} className="flex items-center justify-between border border-border rounded-xl p-3" data-testid={`staff-row-${s.id}`}>
                  <div>
                    <p className="font-semibold text-sm">{s.name} <span className="text-xs font-normal text-muted-foreground">· {s.rut}</span></p>
                    <p className="text-xs text-muted-foreground">{s.email} · {s.role === "superadmin" ? "Superadmin" : s.role === "admin" ? "Administrador" : "Evaluador"} · {s.has_password ? "activo" : "pendiente de crear clave"}</p>
                  </div>
                  {s.role !== "superadmin" && !s.is_self && (
                    <button onClick={() => deleteStaff(s.id, s.name)} className="text-destructive" data-testid={`btn-delete-staff-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {isSuper && (
          <section className="bg-white rounded-2xl border border-border p-6 space-y-4" data-testid="moderation-section">
            <div className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-destructive" /><h2 className="text-lg font-semibold">Contenido marcado</h2></div>
            <p className="text-sm text-muted-foreground">Registros bloqueados por lenguaje de odio/antisemita. Solo tú los ves. Si fue un error, reactiva la cuenta.</p>
            {flags.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="mod-empty">No hay contenido marcado.</p> : (
              <div className="space-y-2">
                {flags.map((f) => (
                  <div key={f.id} className={`border rounded-xl p-3 ${f.resolved ? "border-border opacity-60" : "border-red-200 bg-red-50/50"}`} data-testid={`mod-flag-${f.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-primary">{f.email || f.rut || "—"} <span className="text-xs font-normal text-muted-foreground">· {f.context} · {f.method}</span></p>
                        <p className="text-xs text-red-800 mt-0.5">{f.reason}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2 italic">"{f.text}"</p>
                        <p className="text-[11px] text-muted-foreground mt-1">{new Date(f.created_at).toLocaleString("es-CL")}{f.resolved ? " · reactivado" : ""}</p>
                      </div>
                      {!f.resolved && (
                        <Button size="sm" variant="outline" className="rounded-lg border-2 shrink-0" onClick={() => reactivate(f.id)} data-testid={`btn-reactivate-${f.id}`}>Reactivar</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <div className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-accent" /><h2 className="text-lg font-semibold">Cambiar mi contraseña</h2></div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase font-semibold text-slate-500">Contraseña actual</Label>
              <Input type="password" className="h-12 rounded-xl mt-1.5" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="admin-pw-current" />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold text-slate-500">Nueva contraseña</Label>
              <Input type="password" className="h-12 rounded-xl mt-1.5" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="admin-pw-new" />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold text-slate-500">Repite la nueva</Label>
              <Input type="password" className="h-12 rounded-xl mt-1.5" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="admin-pw-confirm" />
            </div>
          </div>
          <Button onClick={changePassword} disabled={busyPw} variant="outline" className="rounded-xl border-2" data-testid="btn-admin-change-pw">
            {busyPw ? <Loader2 className="w-4 h-4 animate-spin" /> : "Actualizar contraseña"}
          </Button>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <div className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-accent" /><h2 className="text-lg font-semibold">Códigos de respaldo MFA</h2></div>
          <p className="text-sm text-muted-foreground">Genera nuevos códigos de un solo uso para recuperar el acceso si pierdes tu teléfono. Los anteriores dejarán de funcionar.</p>
          <Button onClick={regenCodes} variant="outline" className="rounded-xl border-2" data-testid="btn-regen-backup">Generar nuevos códigos de respaldo</Button>
          {genCodes && (
            <div className="grid grid-cols-2 gap-2 bg-secondary/60 rounded-xl p-4" data-testid="regen-codes">
              {genCodes.map((c) => <span key={c} className="font-mono text-primary text-center py-1">{c}</span>)}
            </div>
          )}
        </section>

        {isSuper && <PasskeySettings />}

        <Button onClick={save} disabled={busy} className="btn-lift h-12 rounded-xl px-8" data-testid="btn-save-config">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Guardar configuración"}
        </Button>
      </div>
    </AdminLayout>
  );
}
